import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type PostCategory = 'NEWS' | 'MATCH_REPORT' | 'INTERVIEW' | 'GALLERY' | 'VIDEO';
export type PostMediaType = 'IMAGE' | 'VIDEO_UPLOAD' | 'VIDEO_YOUTUBE';

export interface PostMediaDto {
  id: string;
  type: PostMediaType;
  url: string;
  thumbnailUrl: string | null;
  duration: number | null;
  position: number;
}

export interface PostListItemDto {
  id: string;
  title: { it?: string; en?: string };
  coverImage: string | null;
  category: PostCategory;
  likesCount: number;
  publishedAt: string | null;
}

export interface PostDetailDto extends PostListItemDto {
  body: { it?: string; en?: string };
  media: PostMediaDto[];
  isLikedByMe: boolean;
  authorId: string;
  isPublished: boolean;
  createdAt: string;
}

export interface CreatePostInput {
  title: { it?: string; en?: string };
  body: { it?: string; en?: string };
  coverImage?: string;
  category: PostCategory;
  isPublished?: boolean;
  media?: Array<{
    type: PostMediaType;
    url: string;
    thumbnailUrl?: string;
    duration?: number;
    position?: number;
  }>;
}

export type UpdatePostInput = Partial<CreatePostInput>;

export interface PaginatedPosts {
  data: PostListItemDto[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

interface PostsFilters {
  page?: number;
  limit?: number;
  category?: PostCategory;
  includeDrafts?: boolean;
}

const QK = {
  list: (filters: PostsFilters) => ['posts', filters] as const,
  one: (id: string) => ['posts', id] as const,
};

export function usePosts(filters: PostsFilters = {}) {
  return useQuery({
    queryKey: QK.list(filters),
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = {};
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.category) params.category = filters.category;
      if (filters.includeDrafts !== undefined) params.includeDrafts = filters.includeDrafts;
      const res = await apiClient.get<PaginatedPosts>('/posts', { params });
      return res.data;
    },
  });
}

export function usePost(id: string | null) {
  return useQuery({
    queryKey: QK.one(id ?? ''),
    enabled: id !== null,
    queryFn: async () => {
      const res = await apiClient.get<PostDetailDto>(`/posts/${id}`);
      return res.data;
    },
  });
}

export function useCreatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePostInput) => {
      const res = await apiClient.post<PostDetailDto>('/posts', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posts'] }),
  });
}

export function useUpdatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdatePostInput }) => {
      const res = await apiClient.patch<PostDetailDto>(`/posts/${id}`, input);
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['posts'] });
      qc.invalidateQueries({ queryKey: QK.one(vars.id) });
    },
  });
}

export function useDeletePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/posts/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posts'] }),
  });
}
